/**
 * Audio Engine
 *
 * Handles ambient soundscapes, music, and interactive audio.
 * Essential for the Myst-like atmospheric experience.
 */

interface AudioState {
  ambient: HTMLAudioElement | null;
  ambientSrc: string | null;
  effects: Map<string, HTMLAudioElement>;
  muted: boolean;
  volume: number;
  ducked: boolean;
}

const state: AudioState = {
  ambient: null,
  ambientSrc: null,
  effects: new Map(),
  muted: false,
  volume: 0.7,
  ducked: false,
};

export function initAudio(): void {
  // Restore user preferences
  const saved = localStorage.getItem('vampires_audio');
  if (saved) {
    try {
      const prefs = JSON.parse(saved) as { muted: boolean; volume: number };
      state.muted = prefs.muted;
      state.volume = prefs.volume;
    } catch {
      // Corrupted localStorage — reset to defaults and clear
      console.warn('⚠️ Corrupted audio preferences — resetting to defaults');
      localStorage.removeItem('vampires_audio');
    }
  }
}

export function setAmbient(src: string, fadeIn = 2000): void {
  // Same track already playing — skip restart
  if (state.ambientSrc === src && state.ambient) return;

  if (state.ambient) {
    fadeOut(state.ambient, 1000);
  }

  const audio = new Audio(src);
  audio.loop = true;
  audio.volume = 0;
  state.ambient = audio;
  state.ambientSrc = src;

  if (!state.muted) {
    void audio.play().then(() => {
      fadeInAudio(audio, state.ducked ? 0 : state.volume, fadeIn);
    });
  }
}

export function playEffect(id: string, src: string): void {
  if (state.muted) return;

  let audio = state.effects.get(id);
  if (!audio) {
    audio = new Audio(src);
    state.effects.set(id, audio);
  }

  audio.currentTime = 0;
  audio.volume = state.volume;
  void audio.play();
}

export function toggleMute(): boolean {
  state.muted = !state.muted;

  if (state.ambient) {
    if (state.muted) {
      state.ambient.pause();
    } else {
      void state.ambient.play();
    }
  }

  savePrefs();
  return state.muted;
}

export function setVolume(volume: number): void {
  state.volume = Math.max(0, Math.min(1, volume));
  if (state.ambient && !state.ducked) {
    state.ambient.volume = state.volume;
  }
  savePrefs();
}

export function duckAmbient(fadeMs = 1000): void {
  state.ducked = true;
  if (state.ambient && !state.muted) {
    fadeToVolume(state.ambient, 0, fadeMs);
  }
}

export function restoreAmbient(fadeMs = 1000): void {
  state.ducked = false;
  if (state.ambient && !state.muted) {
    fadeToVolume(state.ambient, state.volume, fadeMs);
  }
}

function fadeToVolume(audio: HTMLAudioElement, target: number, duration: number): void {
  const steps = 20;
  const start = audio.volume;
  const delta = (target - start) / steps;
  const interval = duration / steps;
  let step = 0;

  const timer = setInterval(() => {
    step++;
    audio.volume = Math.max(0, Math.min(1, start + delta * step));
    if (step >= steps) {
      clearInterval(timer);
      audio.volume = target;
    }
  }, interval);
}

function fadeInAudio(audio: HTMLAudioElement, targetVolume: number, duration: number): void {
  const steps = 20;
  const increment = targetVolume / steps;
  const interval = duration / steps;
  let current = 0;

  const timer = setInterval(() => {
    current += increment;
    audio.volume = Math.min(current, targetVolume);
    if (current >= targetVolume) {
      clearInterval(timer);
    }
  }, interval);
}

function fadeOut(audio: HTMLAudioElement, duration: number): void {
  const steps = 20;
  const decrement = audio.volume / steps;
  const interval = duration / steps;

  const timer = setInterval(() => {
    audio.volume = Math.max(0, audio.volume - decrement);
    if (audio.volume <= 0) {
      clearInterval(timer);
      audio.pause();
    }
  }, interval);
}

function savePrefs(): void {
  localStorage.setItem(
    'vampires_audio',
    JSON.stringify({
      muted: state.muted,
      volume: state.volume,
    })
  );
}
